import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/contexts/TranslationContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_IMAGE = 'https://customer-assets.emergentagent.com/job_stray-care-1/artifacts/m4tj8pbh_1Smartpaw%20Post%20-%2034.PNG';

const districts = ['Vake', 'Vera', 'Sololaki', 'Mtatsminda', 'Chughureti', 'Saburtalo', 'Digomi'];
const venueTypes = ['Café', 'Restaurant', 'Bar', 'Hotel', 'Hostel', 'Park', 'Shop', 'Petrol Station', 'Dessert Shop / Cafe', 'Marketing/Advertising Agency', 'Other'];

const BecomeHostPage = () => {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useTranslation();
  const [formData, setFormData] = useState({
    venue_name: '',
    venue_type: 'Café',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    district: 'Vake',
    message: '',
    has_pets: false,
    pet_details: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/host-applications`, {
        venue_name: formData.venue_name,
        venue_type: formData.venue_type,
        contact_name: formData.contact_name,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        address: formData.address,
        district: formData.district,
        message: `${formData.message}\n\nPet Information: ${formData.has_pets ? formData.pet_details : 'No pets on premises'}`
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error submitting application:', error);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src={LOGO_IMAGE} alt="SmartPaw Care" className="h-20 w-auto md:h-24" />
          </button>
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2">
              <button 
                onClick={() => setLanguage('en')}
                className={`font-['Manrope'] font-semibold text-sm transition-colors ${language === 'en' ? 'text-[#1A1C19]' : 'text-gray-400 hover:text-[#F4A460]'}`}
              >
                EN
              </button>
              <span className="text-gray-400">|</span>
              <button 
                onClick={() => setLanguage('ge')}
                className={`font-['Manrope'] font-semibold text-sm transition-colors ${language === 'ge' ? 'text-[#1A1C19]' : 'text-gray-400 hover:text-[#F4A460]'}`}
              >
                GE
              </button>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-[#5E635C] hover:text-[#1A1C19] font-['Manrope'] font-medium"
            >
              ← {language === 'ge' ? 'უკან' : 'Back to Home'}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center py-20"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="font-['Outfit'] text-4xl font-bold text-[#1A1C19] mb-4">
                Application Submitted!
              </h2>
              <p className="font-['Manrope'] text-xl text-[#5E635C] mb-8">
                Thank you for your interest in becoming a SmartPaw Care host. We'll review your application and get back to you within 48 hours.
              </p>
              <p className="font-['Manrope'] text-[#5E635C]">
                Redirecting to homepage...
              </p>
            </motion.div>
          ) : (
            <>
              {/* Hero Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-16"
              >
                <h1 className="font-['Outfit'] text-5xl md:text-7xl font-bold text-[#1A1C19] tracking-tight mb-6">
                  Become a Host
                </h1>
                <p className="font-['Manrope'] text-xl md:text-2xl text-[#5E635C] leading-relaxed max-w-3xl mx-auto mb-8">
                  Join our network of compassionate venues making a difference for Georgia's stray dogs. By hosting a SmartPaw Feeder, you'll provide consistent, hygienic care while enhancing your reputation as a pet-friendly business.
                </p>
                <div className="bg-[#FDF0E6] rounded-2xl p-8 max-w-2xl mx-auto">
                  <h3 className="font-['Outfit'] font-semibold text-2xl text-[#1A1C19] mb-4">
                    Why Host a Feeder?
                  </h3>
                  <div className="bg-[#4A90E2]/10 border-l-4 border-[#4A90E2] rounded p-4 mb-6">
                    <p className="font-['Manrope'] text-[#1A1C19] font-semibold mb-2">
                      Zero Cost, Zero Effort for You!
                    </p>
                    <p className="font-['Manrope'] text-[#5E635C] text-sm">
                      You only need to provide outdoor space with an electricity connection. We handle <strong>everything</strong> else: supply, installation, maintenance, cleaning, and all operational costs.
                    </p>
                  </div>
                  <ul className="text-left space-y-3 font-['Manrope'] text-[#5E635C]">
                    <li className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-[#F4A460] flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span><strong>No expense:</strong> We cover all costs including device, food, and maintenance</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-[#F4A460] flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span><strong>No involvement needed:</strong> Automated and self-cleaning system</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-[#F4A460] flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span><strong>Positive PR:</strong> Be featured on our website and social media</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-[#F4A460] flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span><strong>Community impact:</strong> Help reduce street pollution and improve animal welfare</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-[#F4A460] flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span><strong>Attract customers:</strong> Pet lovers will seek out your venue</span>
                    </li>
                  </ul>
                </div>
              </motion.div>

              {/* Application Form */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white rounded-3xl p-10 shadow-[0_8px_30px_rgba(0,0,0,0.08)]"
              >
                <h2 className="font-['Outfit'] text-3xl font-bold text-[#1A1C19] mb-8">
                  Host Application Form
                </h2>

                {error && (
                  <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg font-['Manrope']">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Company Name */}
                  <div>
                    <label className="block font-['Manrope'] font-semibold text-[#1A1C19] mb-2">
                      Company/Venue Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.venue_name}
                      onChange={(e) => setFormData({ ...formData, venue_name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F4A460] focus:border-transparent font-['Manrope']"
                      placeholder="e.g., Mziuri Cafe"
                      data-testid="venue-name-input"
                    />
                  </div>

                  {/* Venue Type and District */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-['Manrope'] font-semibold text-[#1A1C19] mb-2">
                        Venue Type *
                      </label>
                      <select
                        required
                        value={formData.venue_type}
                        onChange={(e) => setFormData({ ...formData, venue_type: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F4A460] focus:border-transparent font-['Manrope']"
                        data-testid="venue-type-select"
                      >
                        {venueTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block font-['Manrope'] font-semibold text-[#1A1C19] mb-2">
                        District *
                      </label>
                      <select
                        required
                        value={formData.district}
                        onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F4A460] focus:border-transparent font-['Manrope']"
                        data-testid="district-select"
                      >
                        {districts.map(district => (
                          <option key={district} value={district}>{district}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block font-['Manrope'] font-semibold text-[#1A1C19] mb-2">
                      Full Address *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F4A460] focus:border-transparent font-['Manrope']"
                      placeholder="e.g., 33 Lado Asatiani St, Tbilisi"
                      data-testid="address-input"
                    />
                  </div>

                  {/* Contact Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-['Manrope'] font-semibold text-[#1A1C19] mb-2">
                        Contact Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F4A460] focus:border-transparent font-['Manrope']"
                        placeholder="Your name"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label className="block font-['Manrope'] font-semibold text-[#1A1C19] mb-2">
                        Contact Phone *
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F4A460] focus:border-transparent font-['Manrope']"
                        placeholder="+995 XXX XXX XXX"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-['Manrope'] font-semibold text-[#1A1C19] mb-2">
                      Contact Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F4A460] focus:border-transparent font-['Manrope']"
                      placeholder="your@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block font-['Manrope'] font-semibold text-[#1A1C19] mb-2">
                      Tell us about your venue
                    </label>
                    <textarea
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F4A460] focus:border-transparent font-['Manrope']"
                      placeholder="Describe your venue, outdoor space, and why you'd like to host a SmartPaw Feeder..."
                      data-testid="venue-description-textarea"
                    />
                  </div>

                  {/* Pet Information */}
                  <div className="bg-[#EBF3FC] rounded-2xl p-6">
                    <h3 className="font-['Outfit'] font-semibold text-xl text-[#1A1C19] mb-4">
                      Pet Information
                    </h3>
                    <div className="mb-4">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.has_pets}
                          onChange={(e) => setFormData({ ...formData, has_pets: e.target.checked })}
                          className="w-5 h-5 text-[#F4A460] border-2 border-gray-300 rounded focus:ring-[#F4A460]"
                          data-testid="has-pets-checkbox"
                        />
                        <span className="font-['Manrope'] text-[#1A1C19]">
                          There are stray dogs or pets around our venue
                        </span>
                      </label>
                    </div>

                    {formData.has_pets && (
                      <div>
                        <label className="block font-['Manrope'] font-semibold text-[#1A1C19] mb-2">
                          Tell us about them
                        </label>
                        <textarea
                          rows={3}
                          value={formData.pet_details}
                          onChange={(e) => setFormData({ ...formData, pet_details: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F4A460] focus:border-transparent font-['Manrope']"
                          placeholder="How many dogs? Are they friendly? Any specific care needs?"
                          data-testid="pet-details-textarea"
                        />
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-8 py-4 bg-[#F4A460] text-white rounded-full font-['Manrope'] font-semibold text-lg hover:bg-[#E89450] disabled:bg-gray-400 transform hover:scale-105 transition-all duration-200 shadow-[0_8px_30px_rgba(244,164,96,0.3)]"
                    data-testid="submit-host-application"
                  >
                    {loading ? 'Submitting...' : 'Submit Application'}
                  </button>
                </form>
              </motion.div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BecomeHostPage;
